// #ux-redesign-start: InboxViewForm — copia del de Doctors adaptada a Patients.
// Look & feel del DS (Quill WYSIWYG + AttachmentPicker/Chip + sanitización),
// PERO conservando el flow específico de Patients:
//
//   - Patients sólo envía a UN doctor (no multi). El AsyncSelect tipo single
//     (InboxAsyncRecipients de Patients) busca via PatientsService.searchDoctor.
//   - El backend (MessagesPatientsController.addProxy) fuerza `isNew=true` y
//     redirige el mensaje a todos los `secretaries` con `patient_reply_permission`.
//     El doctor elegido por el paciente se prefija en el body server-side.
//   - Para `reply` el destinatario es message.from (siempre un doctor), no
//     editable. Por eso no permitimos "add recipient" como en Doctors — el
//     paciente nunca puede dirigirse a otro paciente.
// ─────────────────────────────────────────────────────────────────────────────

import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormGroup, Label, Button } from 'reactstrap';
import { useForm } from 'react-hook-form';
import { useTranslate } from 'react-translate';
import moment from 'moment';

import { LoadingBlock, toast, ConfirmDialog } from 'components/ds';
import AttachmentPicker from 'components/Messages/Actions/AttachmentPicker';
import AttachmentChip from 'components/Messages/View/AttachmentChip';
import MultimediaService from 'services/MultimediaService';
import fileRules from 'plugins/TS-lib-utils-public/configs/fileRules';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sanitizeMessageHtml } from 'helpers/sanitizeMessage';

import { actionSendMessage } from 'store/actions/messagesActions';
import InboxAsyncRecipients from './InboxAsyncRecipients';

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
};

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const InboxViewForm = ({ setShowForm, reply, resend }) => {
  const t = useTranslate('messages');
  const dispatch = useDispatch();

  const box = useSelector((state) => state.messagesReducer.box);
  const users = useSelector((state) => state.userDataReducer.users);
  const { message, loading_msg } = useSelector(
    (state) => state.messagesReducer
  );

  // Archivos en state local como File[]. AttachmentPicker llama onPick(file).
  const [files, setFiles] = useState([]);

  const addFile = (f) => {
    setFiles((prev) => {
      const key = `${f.name}|${f.size}|${f.lastModified || 0}`;
      const exists = prev.some(
        (p) => `${p.name}|${p.size}|${p.lastModified || 0}` === key
      );
      return exists ? prev : [...prev, f];
    });
  };

  const removeFileAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Resend: re-fetch los archivos del mensaje original como File reales.
  useEffect(() => {
    if (!resend || !message?.files?.length) return;
    let cancelled = false;
    (async () => {
      const fetched = await Promise.all(
        message.files.map(async (fileId) => {
          try {
            const meta = await MultimediaService.getFileInfo(fileId);
            const url = await MultimediaService.getFile(fileId);
            if (!url || !meta) return null;
            const blob = await (await fetch(url)).blob();
            return new File([blob], meta.name || `file-${fileId}`, {
              type: meta.mimetype || blob.type,
            });
          } catch (e) {
            return null;
          }
        })
      );
      if (cancelled) return;
      const valid = fetched.filter(Boolean);
      if (valid.length) setFiles((prev) => [...valid, ...prev]);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resend]);

  const defaultValues = {
    // #ux-redesign: solo prefijar "Re:" cuando hay subject real. Si el usuario
    // entra a /admin/inbox/reply desde un punto que no es una respuesta (p. ej.
    // botón de mensaje desde /admin/doctors), message.subject viene undefined
    // y antes quedaba "Re: undefined" en el campo.
    subject: message && message.subject ? `Re: ${message.subject}` : '',
    body: message && message.body && !resend
      ? '\n------' +
        '\nFrom: ' +
        (message.from.title ? message.from.title + ' ' : '') +
        message.from.firstName + ' ' + message.from.lastName +
        '\nSubject: ' +
        message.subject +
        '\nDate: ' +
        moment(message.date).format('L HH:mm') +
        '\n' +
        message.body
      : '',
  };

  const { register, handleSubmit, control, errors, setValue } = useForm({
    defaultValues,
  });

  // Quill controlado + sync con react-hook-form via campo hidden 'body'.
  const [bodyHtml, setBodyHtml] = useState(defaultValues.body || '');
  useEffect(() => {
    setBodyHtml(defaultValues.body || '');
    setValue('body', defaultValues.body || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);
  const handleBodyChange = (html) => {
    const normalized = html === '<p><br></p>' ? '' : html;
    setBodyHtml(normalized);
    setValue('body', normalized ? sanitizeMessageHtml(normalized) : '');
  };

  // ConfirmDialog informativo post-envío.
  const [sentDialogOpen, setSentDialogOpen] = useState(false);

  if (loading_msg) {
    return <LoadingBlock />;
  }

  // MessagesService.send espera `attachments` como FileList-like (toma [0] de
  // cada slot). Envolvemos cada File en su propio array para mantener compat.
  const withAttachments = (payload) => ({
    ...payload,
    attachments: files.map((f) => {
      //Encode filename and create new file Blob
      const encodedName = encodeURIComponent(f.name);
      const blob = f.slice(0, f.size, f.type);
      const newFile = new File([blob], encodedName, {type: f.type});

      return [newFile];
    }),
  });

  const onSubmit = (data) => {
    console.log(data);
    console.log(message);

    // Caso A: mensaje nuevo desde el AsyncSelect (Patients siempre single).
    // InboxAsyncRecipients inyecta `isNew=true` como hidden input cuando es
    // creación desde cero.
    if (data.hasOwnProperty('isNew')) {
      const _id = data.to && data.to.value ? data.to.value : data.to;
      const recipient = users.find(
        (user) =>
          _id === user.auth_user_id ||
          _id === `${user.firstName} ${user.lastName}`
      );
      if (!recipient) {
        return toast.error(t('alert-error-recipient-invalid'));
      }
      const to = JSON.stringify([
        {
          user_id: recipient.auth_user_id,
          type: recipient.type || 'doctor',
        },
      ]);
      dispatch(
        actionSendMessage(
          withAttachments({ ...data, to }),
          recipient.practice ? recipient.practice._id : false
        )
      );
      setSentDialogOpen(true);
      setShowForm(false);
      return null;
    }

    // Caso B: reply (botón Reply del toolbar). El destinatario es siempre el
    // sender original del mensaje recibido (un doctor).
    const to = JSON.stringify([
      {
        user_id: message.from.auth_user_id,
        type: message.from.type || 'doctor',
      },
    ]);
    const message_answered = JSON.stringify({
      _id: message.message_global_id,
      box,
      date: message.date,
    });
    dispatch(
      actionSendMessage(
        withAttachments({ ...data, to, message_answered }),
        message.practice ? message.practice._id : false
      )
    );
    setSentDialogOpen(true);
    setShowForm(false);
  };

  return (
    <Fragment>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label for="to">{t('recipients')}</Label>
          {message && !resend && !reply ? (
            <input
              type="text"
              className="form-control"
              readOnly
              defaultValue={
                message.from.firstName + ' ' + message.from.lastName
              }
            />
          ) : (
            <InboxAsyncRecipients
              register={register}
              control={control}
              focus
            />
          )}
        </FormGroup>

        <FormGroup>
          <Label for="subject">{t('subject')}</Label>
          <input
            className="form-control"
            type="text"
            name="subject"
            id="subject"
            placeholder={t('subject')}
            ref={register({ required: true, maxLength: 255 })}
          />
          {errors.subject && (
            <p className="alert alert-danger mt-2 w-50">
              {t('error-subject')}
            </p>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="message">{t('message')}</Label>
          <div className="inbox-msg-form-body inbox-msg-form-body--rich">
            <ReactQuill
              theme="snow"
              value={bodyHtml}
              onChange={handleBodyChange}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder={t('message-placeholder') || 'Write your message...'}
            />
          </div>
          <input type="hidden" name="body" ref={register} />
          {errors.body && (
            <div className="alert alert-danger mt-2 w-50">
              {t('error-message')}
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label>{t('attachments')}</Label>
          <div className="inbox-attachments-section">
            {files.length > 0 && (
              <div className="inbox-attachments-grid">
                {files.map((f, i) => (
                  <AttachmentChip
                    key={`${f.name}|${f.size}|${f.lastModified || i}`}
                    file={f}
                    onRemove={() => removeFileAt(i)}
                  />
                ))}
              </div>
            )}
            <AttachmentPicker onPick={addFile} />
          </div>
        </FormGroup>

        <Label className="inbox-max-size-hint">
          {`${t('max-file-size') || 'Max'}: ${fileRules.maxSize} Megabytes (MB)`}
        </Label>

        <div className="inbox-form-footer">
          <Button
            size="sm"
            id="btn-cancel"
            type="reset"
            className="btn-primary"
            onClick={(e) => {
              e.preventDefault();
              setShowForm(false);
            }}
          >
            {t('btn-cancel')}{' '}
            <i className="fas fa-times" aria-hidden="true"></i>
          </Button>
          <Button id="btn-reply" size="sm" className="btn-info">
            {t('btn-send')} <span className="icon-send"></span>
          </Button>
        </div>
      </Form>

      <ConfirmDialog
        isOpen={sentDialogOpen}
        title={t('please-be-patient')}
        confirmLabel={t('btn-ok')}
        cancelLabel={t('btn-ok')}
        variant="primary"
        onConfirm={() => setSentDialogOpen(false)}
        onCancel={() => setSentDialogOpen(false)}
      />
    </Fragment>
  );
};

export default InboxViewForm;
// #ux-redesign-end
